// The solver page's simulation, kept for the tab while the person fixes a record a lead opened: the
// selection, the parameters and the diagnosis come back as they were, and the page reads again what
// the leads asked for. Kept in sessionStorage — one tab, gone with it — and read defensively: a
// session from an older page, or a storage that refuses, is no session at all.

import type { SolverDiagnostics } from '@/composables/use-scheduling-solver';
import type {
  SchedulingSolverError, SchedulingSolverResponse, SolveGroupsRequest, SolvePrecheckPoint, SolverRemedy,
} from '@/sdk/model/scheduling';

export const SOLVER_SESSION_KEY = 'parcours.solver.session';
export const SOLVER_RETURN_KEY = 'parcours.solver.return';

/** The parameters panel, field by field. */
export interface SolverParameters {
  maxTeacherHours: number | null;
  includeConfirmedGroups: boolean;
  includeUnconfirmedGroups: boolean;
  preserveModuleOrder: boolean;
  requireSingleTeacherPerModule: boolean;
  requireSingleRoomPerModule: boolean;
  includeVacancies: boolean;
  autoCreateVacancies: boolean;
}

export interface SolverSession {
  version: 1;
  selectedGroupIds: string[];
  parameters: SolverParameters;
  /** The groups of the last run: the ones the diagnosis is about. */
  lastSolvedGroupIds: string[];
  /** The request of the last run: what a lead's check rebuilds. */
  request: SolveGroupsRequest | null;
  /** The refusal on screen, with its leads. */
  refusal: SchedulingSolverError | null;
  /** The record a lead opened, when the person chose one among several (a teacher's frame). */
  openedSubject?: string;
  /** The changes whose record a lead opened, by remedy key (simulation-checks.ts). */
  openedKeys?: string[];
  /** The pre-check as the person left it: what it can call settled on return. */
  precheck?: SolverPrecheckSession;
  /** The point whose solution the person followed: the page goes back to it on return. */
  openedPoint?: string;
}

/**
 * The schedules found, kept in memory while the person fixes a record a solution opened: they come back with the
 * simulation. Not in the tab's storage — a large run's schedules would outgrow it, and a reload is a new simulation.
 */
export interface KeptSolutions {
  response: SchedulingSolverResponse;
  active: number;
  /** The groups they were computed for, and the request: what « Autre solution » excludes them from. */
  groupIds: string[];
  request: SolveGroupsRequest | null;
  /** Each solution's own history, as the result shows it under its tab. */
  progress: string[][];
  diagnostics: (SolverDiagnostics | null)[];
}

/** What the pre-check keeps for the way back (use-solver-precheck.ts). */
export interface SolverPrecheckSession {
  /** The blocking points shown since the last run: one gone from the next answer is settled. */
  points: SolvePrecheckPoint[];
  /** The record fixes opened from them, which the next check reads. */
  opened: SolverRemedy[];
  /** The fixes made on a point that stayed open for the rest, by point key: credited there on return. */
  made?: Record<string, SolverRemedy[]>;
  /** The points were on screen, a run held back: they show again on return. */
  shown?: boolean;
  /** The groups « Générer » was pressed for when they showed: the points speak of these only. */
  groupIds?: string[];
}

/** Where a lead took the person, and from which simulation: that page offers the way back. */
export interface SolverReturn {
  path: string;
}

const BOOLEAN_PARAMETERS = [
  'includeConfirmedGroups', 'includeUnconfirmedGroups', 'preserveModuleOrder', 'requireSingleTeacherPerModule',
  'requireSingleRoomPerModule', 'includeVacancies', 'autoCreateVacancies',
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const isStrings = (value: unknown): value is string[] => Array.isArray(value) && value.every((v) => typeof v === 'string');

function parseParameters(value: unknown): SolverParameters | null {
  if (!isRecord(value)) return null;
  // A session an older page wrote may still carry its time limit: the solve no longer takes one.
  const { maxTeacherHours } = value;
  if (!(maxTeacherHours === null || (typeof maxTeacherHours === 'number' && Number.isFinite(maxTeacherHours)))) return null;
  if (!BOOLEAN_PARAMETERS.every((key) => typeof value[key] === 'boolean')) return null;
  return value as unknown as SolverParameters;
}

const isNamed = (value: unknown): boolean => isRecord(value) && typeof value.id === 'string' && typeof value.name === 'string';
const isRemedy = (value: unknown): value is SolverRemedy => isRecord(value) && typeof value.kind === 'string';
const isPoint = (value: unknown): value is SolvePrecheckPoint => isRecord(value)
  && typeof value.key === 'string' && typeof value.kind === 'string' && isNamed(value.competency) && isNamed(value.program)
  && Array.isArray(value.groups) && value.groups.every(isNamed) && Array.isArray(value.fixes) && value.fixes.every(isRemedy);

/** The fixes made per point, keeping only what is a fix; none when an older page kept none. */
function parseMade(value: unknown): Record<string, SolverRemedy[]> | undefined {
  if (!isRecord(value)) return undefined;
  return Object.fromEntries(Object.entries(value)
    .map(([key, remedies]) => [key, Array.isArray(remedies) ? remedies.filter(isRemedy) : []] as const)
    .filter(([, remedies]) => remedies.length > 0));
}

/** The pre-check kept, keeping only the points and fixes this page can read; none when it was not one. */
function parsePrecheck(value: unknown): SolverPrecheckSession | undefined {
  if (!isRecord(value) || !Array.isArray(value.points) || !Array.isArray(value.opened)) return undefined;
  return {
    points: value.points.filter(isPoint), opened: value.opened.filter(isRemedy), made: parseMade(value.made),
    ...(value.shown === true && { shown: true }),
    ...(isStrings(value.groupIds) && { groupIds: value.groupIds }),
  };
}

/** A stored session, or null when it is not one this page wrote. */
export function parseSession(value: unknown): SolverSession | null {
  if (!isRecord(value) || value.version !== 1) return null;
  if (!isStrings(value.selectedGroupIds) || !isStrings(value.lastSolvedGroupIds)) return null;
  const parameters = parseParameters(value.parameters);
  if (!parameters) return null;
  const request = isRecord(value.request) && isStrings(value.request.groupIds) ? value.request as unknown as SolveGroupsRequest : null;
  const refusal = isRecord(value.refusal) && typeof value.refusal.code === 'string' ? value.refusal as unknown as SchedulingSolverError : null;
  return {
    version: 1,
    selectedGroupIds: value.selectedGroupIds,
    parameters,
    lastSolvedGroupIds: value.lastSolvedGroupIds,
    request,
    // A refusal without the request it answers cannot be checked or rerun: it is not restored.
    refusal: request ? refusal : null,
    openedSubject: typeof value.openedSubject === 'string' ? value.openedSubject : undefined,
    openedKeys: isStrings(value.openedKeys) ? value.openedKeys : undefined,
    precheck: parsePrecheck(value.precheck),
    openedPoint: typeof value.openedPoint === 'string' ? value.openedPoint : undefined,
  };
}

export function parseReturn(value: unknown): SolverReturn | null {
  return isRecord(value) && typeof value.path === 'string' && value.path.startsWith('/') ? { path: value.path } : null;
}

/** sessionStorage, when the page may use it: a private window or a blocked site gives none. */
export function sessionStore(): Storage | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

export function readStored<T>(storage: Storage | undefined, key: string, parse: (value: unknown) => T | null): T | null {
  try {
    const raw = storage?.getItem(key);
    return raw ? parse(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** Writes, or removes when null; a storage that refuses (quota, private window) loses the session, nothing else. */
export function writeStored(storage: Storage | undefined, key: string, value: unknown): void {
  try {
    if (value == null) storage?.removeItem(key);
    else storage?.setItem(key, JSON.stringify(value));
  } catch {
    // Kept in memory for this page only.
  }
}

/** The selection to restore: the saved groups the page still offers to plan, in its own order. */
export function restoredSelection(saved: readonly string[], offered: readonly string[]): Set<string> {
  const wanted = new Set(saved);
  return new Set(offered.filter((id) => wanted.has(id)));
}
